import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { SolEstimatePricingMode, SubSidyProgramMaterialType } from '../../../models/enum';
import { MatButtonModule } from '@angular/material/button';
import { SolEstimateTableMaterialCostsRowCollection } from '../../../models/sol-estimate-table/SolEstimateTableMaterialCostsRowCollection';
import { MatInputModule } from '@angular/material/input';
import { SolEstimateTableMaterialCostsRow } from '../../../models/sol-estimate-table/SolEstimateTableMaterialCostsRow';
import { SolEstimateTableMaterialTypeComponent } from '../sol-estimate-table-material-type/sol-estimate-table-material-type.component';
import { MultilingualCurrencyPipe } from "../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe";
import { DecimalPrecisionPipe } from "../../../pipes/decimal-precision-pipe/decimal-precision.pipe";
import { CommonDataService } from '../../../services/common-data.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-sol-estimate-table-material-costs',
  imports: [
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    CommonModule,
    TranslatePipe,
    SolEstimateTableMaterialTypeComponent,
    MultilingualCurrencyPipe,
    DecimalPrecisionPipe
],
  templateUrl: './sol-estimate-table-material-costs.component.html',
  styleUrl: './sol-estimate-table-material-costs.component.scss'
})
export class SolEstimateTableMaterialCostsComponent implements OnInit, OnDestroy {

  isMaterialCostsExpanded: boolean = false;
  isSiemensMaterialExpanded: boolean = false;
  isThirdPartyMaterialExpanded: boolean = false;
  isOutsideMaterialExpanded: boolean = false;
  isMechanicalMaterialExpanded: boolean = false;
  isElectricalMaterialExpanded: boolean = false;

  @Input() isMarkUpSelected: boolean = false;
  @Input() sellAsPricingMode: SolEstimatePricingMode = SolEstimatePricingMode.Line_Item;
  @Input() materialCostsFormGroup!: FormGroup<SolEstimateTableMaterialCostsRowCollection>;
  @Input() isCollaborativeNotEdit: boolean = false;
  

  
  solEstimatePricingMode = SolEstimatePricingMode;
  private readonly destroy$ = new Subject<void>();
  
  constructor(
    private readonly commonDataService: CommonDataService
  ) { }

  ngOnInit(): void {
    if(!this.materialCostsFormGroup){
      return;
    }
    this.commonDataService.subsidyChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(subsidy => this.updateSubsidyIndicator(subsidy?.subsidy ?? 0, subsidy?.subsidyMaterial ?? 0));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateSubsidyIndicator(subsidyValue: number, subsidyMaterialType: number): void {
    this.clearSubsidyIndicators();
    if (subsidyValue <= 0) {
      return;
    }

    switch(subsidyMaterialType){
      case SubSidyProgramMaterialType.Mechanical_Material:
        this.setSubsidyIndicator(this.materialCostsFormGroup.controls.mechanicalMaterial, subsidyValue);
        break;
      case SubSidyProgramMaterialType.Siemens_Material:
        this.setSubsidyIndicator(this.materialCostsFormGroup.controls.siemensMaterial, subsidyValue);
        break;
      case SubSidyProgramMaterialType.Outside_Material:
        this.setSubsidyIndicator(this.materialCostsFormGroup.controls.outsideMaterial, subsidyValue);
        break;
      case SubSidyProgramMaterialType.Electrical_Material:
        this.setSubsidyIndicator(this.materialCostsFormGroup.controls.electricalMaterial, subsidyValue);
        break;
    }
  }

  private setSubsidyIndicator(materialRowForm: FormGroup<SolEstimateTableMaterialCostsRow>, subsidyValue: number): void {
    materialRowForm?.controls.isSubsidyValuePresent?.patchValue(subsidyValue > 0);
  }

  private clearSubsidyIndicators(): void {
    for (const materialRowForm of [
      this.materialCostsFormGroup.controls.siemensMaterial,
      this.materialCostsFormGroup.controls.outsideMaterial,
      this.materialCostsFormGroup.controls.mechanicalMaterial,
      this.materialCostsFormGroup.controls.electricalMaterial
    ]) {
      materialRowForm.controls.isSubsidyValuePresent?.patchValue(false);
    }
  }

  get siemensMaterialFormGroup(): FormGroup<SolEstimateTableMaterialCostsRow> | null {
    return this.materialCostsFormGroup?.controls.siemensMaterial ?? null; 
  }
  get outsideMaterialFormGroup(): FormGroup<SolEstimateTableMaterialCostsRow> | null {
    return this.materialCostsFormGroup?.controls.outsideMaterial ?? null;
  }
  get mechanicalMaterialFormGroup(): FormGroup<SolEstimateTableMaterialCostsRow> | null {
    return this.materialCostsFormGroup?.controls.mechanicalMaterial ?? null;
  }
  get electricalMaterialFormGroup(): FormGroup<SolEstimateTableMaterialCostsRow> | null {
    return this.materialCostsFormGroup?.controls.electricalMaterial ?? null;
  }

}
